import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { clinicData } from '../data';
import { Helmet } from 'react-helmet-async';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    reason: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Message sent successfully! We will contact you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Appointment request submitted! We will confirm shortly.');
      setAppointmentForm({ name: '', phone: '', date: '', time: '', reason: '' });
    } catch (error) {
      toast.error('Failed to submit appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppCall = () => {
    window.open(`https://wa.me/91${clinicData.contact.whatsappNumber}?text=Hello, I would like to book an appointment`, '_blank');
  };

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>Contact - {clinicData.name} | Men's Health & Andrology</title>
        <meta name="description" content={clinicData.seo.contact.description} />
        <link rel="canonical" href="https://www.manclinics.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-50 via-white to-pink-50 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#D91656]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#1E5A8E]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="max-w-7xl mx-auto px-4 text-center max-w-3xl relative z-10">
          <div className="inline-block bg-[#1E5A8E]/10 text-[#1E5A8E] px-4 py-2 rounded-full text-sm font-medium mb-6">
            Get In Touch
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contact <span className="text-[#D91656]">{clinicData.name}</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Schedule your confidential consultation or reach out with any questions. We're here to help you.
          </p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <CardContent className="p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#D91656] to-[#B01348] rounded-full group-hover:scale-110 transition-transform">
                  <Phone className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Call Us</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <a href={`tel:${clinicData.contact.phone}`} className="block hover:text-[#D91656] transition-colors">
                    {clinicData.contact.phone}
                  </a>
                  <a href={`tel:${clinicData.contact.alternatePhone}`} className="block hover:text-[#D91656] transition-colors">
                    {clinicData.contact.alternatePhone}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <CardContent className="p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#1E5A8E] to-[#164A75] rounded-full group-hover:scale-110 transition-transform">
                  <Mail className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Email Us</h3>
                <a href={`mailto:${clinicData.contact.email}`} className="text-sm text-gray-600 hover:text-[#1E5A8E] transition-colors">
                  {clinicData.contact.email}
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <CardContent className="p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#D91656] to-[#B01348] rounded-full group-hover:scale-110 transition-transform">
                  <MapPin className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Visit Us</h3>
                <p className="text-sm text-gray-600">{clinicData.contact.address}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <CardContent className="p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#1E5A8E] to-[#164A75] rounded-full group-hover:scale-110 transition-transform">
                  <Clock className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Clinic Hours</h3>
                <div className="text-sm text-gray-600">
                  <p>Mon-Sat: 9AM-8PM</p>
                  <p>Sun: By Appointment</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Appointment Form */}
            <Card className="border-none shadow-xl group hover:shadow-2xl transition-all duration-300">
              <div className="bg-gradient-to-r from-[#D91656] to-[#B01348] p-6 rounded-t-xl">
                <h2 className="text-2xl font-bold text-white">Book an Appointment</h2>
              </div>
              <CardContent className="p-8">
                <form onSubmit={handleAppointmentSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="apt-name">Full Name *</Label>
                    <Input
                      id="apt-name"
                      type="text"
                      required
                      value={appointmentForm.name}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, name: e.target.value })}
                      placeholder="Enter your full name"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="apt-phone">Phone Number *</Label>
                    <Input
                      id="apt-phone"
                      type="tel"
                      required
                      value={appointmentForm.phone}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, phone: e.target.value })}
                      placeholder="Enter your phone number"
                      className="mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="apt-date">Preferred Date *</Label>
                      <Input
                        id="apt-date"
                        type="date"
                        required
                        value={appointmentForm.date}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="apt-time">Preferred Time *</Label>
                      <Input
                        id="apt-time"
                        type="time"
                        required
                        value={appointmentForm.time}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="apt-reason">Reason for Visit</Label>
                    <Textarea
                      id="apt-reason"
                      value={appointmentForm.reason}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, reason: e.target.value })}
                      placeholder="Brief description of your concern (optional)"
                      className="mt-2 min-h-[100px]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#D91656] to-[#B01348] hover:from-[#B01348] hover:to-[#D91656] text-white"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {isSubmitting ? 'Submitting...' : 'Submit Appointment Request'}
                  </Button>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-3">Or book instantly via WhatsApp</p>
                    <Button
                      type="button"
                      onClick={handleWhatsAppCall}
                      variant="outline"
                      className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                    >
                      WhatsApp Us
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card className="border-none shadow-xl group hover:shadow-2xl transition-all duration-300">
              <div className="bg-gradient-to-r from-[#1E5A8E] to-[#164A75] p-6 rounded-t-xl">
                <h2 className="text-2xl font-bold text-white">Send Us a Message</h2>
              </div>
              <CardContent className="p-8">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="contact-name">Full Name *</Label>
                    <Input
                      id="contact-name"
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="Enter your full name"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email Address *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="Enter your email"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-phone">Phone Number *</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      required
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="Enter your phone number"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="How can we help you?"
                      className="mt-2 min-h-[150px]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#1E5A8E] to-[#164A75] hover:from-[#164A75] hover:to-[#1E5A8E] text-white"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Find Us on <span className="text-[#D91656]">Map</span>
          </h2>
          <Card className="border-none shadow-xl overflow-hidden">
            <div className="aspect-[16/9] w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3800!2d83.3!3d17.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDQyJzAwLjAiTiA4M8KwMTgnMDAuMCJF!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MAN CLINICS Location"
              ></iframe>
            </div>
          </Card>
          <div className="text-center mt-8">
            <a
              href={clinicData.contact.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-[#1E5A8E] text-[#1E5A8E] hover:bg-[#1E5A8E] hover:text-white">
                <MapPin className="mr-2 h-4 w-4" />
                Get Directions
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#1E5A8E] to-[#D91656]">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Have Questions? We're Here to Help
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Don't hesitate to reach out. Our team is ready to assist you with any inquiries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsAppCall}
              size="lg"
              className="bg-white text-[#D91656] hover:bg-gray-100"
            >
              Chat on WhatsApp
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => window.location.href = `tel:${clinicData.contact.phone}`}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

